
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { MarkdownChatMessageVm } from '@/interfaces/MarkdownChatMessageVm';
import { ParamSuggestAiTextRevision } from '@/interfaces/ParamSuggestAiTextRevision';
import { namespace } from 'vuex-class';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import rest from '@/rest';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// components
import MarkdownViewer from '../Shared/MarkdownViewer.vue';
import SimpleMessageBox from '../Shared/SimpleMessageBox.vue';

const globals = namespace("globals");
const ux = namespace("ux");

@Component({
  components: {
    MarkdownViewer,
    SimpleMessageBox
  }
})
export default class MarkdownAiChatDlg extends Vue {
  @PropSync("showDialog") syncedShowDialog!: boolean;
  @PropSync("content") syncedContent!: string;

  @globals.Getter selectedLanguageCode!: string | null;
  @ux.Getter gradientBackground2: any;

  chatMessages: MarkdownChatMessageVm[] = [];
  selectedMessage: MarkdownChatMessageVm | null = null;
  newMessage: string = '';
  isLoading: boolean = false;
  showConfirmationDialog: boolean = false;

  textEditingPrompts: Array<{ icon: string, title: string, value: string }> = [
    { icon: "mdi-arrow-collapse-vertical", title: "Verkürzen", value: "Bitte kürze den folgenden Text prägnant und verständlich. " },
    { icon: "mdi-arrow-expand-vertical", title: "Erweitern", value: "Bitte erweitere und ergänze den Text inhaltlich sinnvoll. " },
    { icon: "mdi-text-box-check-outline", title: "Vervollständigen", value: "Bitte vervollständige den unvollständigen Text sinnvoll. " },
    { icon: "mdi-spellcheck", title: "Überprüfen", value: "Bitte überprüfe den Text auf Grammatik-, Rechtschreib- und Zeichensetzungsfehler. " },
    { icon: "mdi-format-list-bulleted", title: "Aufzählung", value: "Bitte wandle den Inhalt in eine stichpunktartige Aufzählung um. " },
    { icon: "mdi-format-align-left", title: "Strukturieren", value: "Bitte wandle den Inhalt in eine klare Struktur mit Überschriften und Absätzen um. Der Inhalt sollte dabei ungekürzt und so genau wie möglich wiedergegeben werden. " },
    { icon: "mdi-language-markdown-outline", title: "MD Strukturieren", value: "Bitte formatiere den folgenden Text im Markdown-Format. Verwende # für Hauptüberschriften (H1), ## für Unterüberschriften (H2) und ### für Unter-Unterabschnitte (H3). Nutze Leerzeilen, um Absätze zu trennen. Füge bei Bedarf Listen (mit - oder *) hinzu, um Informationen übersichtlich darzustellen. Behalte den ursprünglichen Textinhalt vollständig bei. " },
    { icon: "mdi-format-section", title: "Automatisch gliedern", value: "Bitte analysiere den Text und erstelle eine sinnvolle Gliederung mit passenden Überschriften." },
    { icon: "mdi-table", title: "Auflistungen in Tabellen wandeln", value: "Wandle die Auflistungen im Text in übersichtliche Tabellen mit passenden Überschriften und Spalten um." },
    // Storytelling / Kreative Umformulierung
    // Nutze STORYTELLING und ergänze diese Inhalte um eine oder mehrere kurze Geschichten oder ein Szenario, um die Zielgruppe emotional zu involvieren und das Verständnis zu fördern! Verwende Charaktere, Konflikte und eine Handlung, um die Inhalte lebendig und nachvollziehbar zu vermitteln.
    // Formuliere den Text in einer erzählerischen Weise, um eine fesselnde Geschichte zu erstellen.
    { icon: "mdi-book-open-outline", title: "Storytelling", value: "Nutze STORYTELLING und ergänze diese Inhalte um eine oder mehrere kurze Geschichten oder ein Szenario, um die Zielgruppe emotional zu involvieren und das Verständnis zu fördern! Verwende Charaktere, Konflikte und eine Handlung, um die Inhalte lebendig und nachvollziehbar zu vermitteln. " },
    { icon: "mdi-file-document-outline", title: "Zusammenfassen", value: "Bitte fasse den Text prägnant und verständlich zusammen. " },
    { icon: "mdi-sync", title: "Paraphrasieren", value: "Bitte formuliere den folgenden Text um, ohne den Inhalt zu verändern. Achte dabei auf klare und verständliche Sprache." },
    { icon: "mdi-translate", title: "Übersetzen", value: "Bitte übersetze den Text ins Englische." },
    { icon: "mdi-account-voice", title: "Klar / Direkt", value: "Formuliere den Text klar, direkt und leicht verständlich, ohne unnötige Ausschmückungen. " },
    { icon: "mdi-emoticon-happy-outline", title: "Freundlich / Einladend", value: "Formuliere den Text freundlich, einladend und zugänglich - so, als würdest du dich an eine interessierte, aber nicht formelle Zielgruppe wenden. " },
    { icon: "mdi-emoticon-neutral-outline", title: "Höflich / Zurückhaltend", value: "Formuliere den Text höflich und zurückhaltend, um Kritik oder heikle Themen sensibel auszudrücken. " },
    { icon: "mdi-emoticon-excited-outline", title: "Locker mit Emojis", value: "Formuliere den Text locker, umgangssprachlich und füge passende Emojis hinzu, damit er freundlich und nahbar wirkt. 🎉😊" },
    { icon: "mdi-bullhorn-outline", title: "Überzeugend / Werbend", value: "Formuliere den Text überzeugend und werbend, mit Fokus auf die Vorteile und einer klaren Handlungsaufforderung. " },
    { icon: "mdi-briefcase-outline", title: "Professionell / Geschäftlich", value: "Formuliere den folgenden Text in einem professionellen, geschäftlichen Tonfall, geeignet für formelle Kommunikation. " },
    { icon: "mdi-account-group-outline", title: "Locker / Umgänglich", value: "Schreibe den Text in einem lockeren, umgangssprachlichen Stil, als würdest du mit einem Freund sprechen. " },
    { icon: "mdi-hospital-box-outline", title: "Medizinisch / Fachlich-patientenorientiert", value: "Formuliere den Text im medizinischen Stil - fachlich korrekt, seriös und präzise, dabei jedoch verständlich und empathisch für medizinisches Fachpersonal oder Patient:innen. " },
    { icon: "mdi-feather", title: "Kreativ / Literarisch", value: "Formuliere den Text kreativ und literarisch, mit anschaulichen Bildern und emotionaler Sprache. " },
    { icon: "mdi-scale-balance", title: "Juristisch / Vertragskonform", value: "Formuliere den Text im juristischen Stil, mit rechtlich eindeutiger und formeller Ausdrucksweise. " },
    { icon: "mdi-school-outline", title: "Wissenschaftlich / Fachlich", value: "Formuliere den Text im Stil eines wissenschaftlichen Artikels, mit klarer Struktur, Fachbegriffen und neutraler Sprache. " },
    { icon: "mdi-code-tags", title: "Technisch / Entwicklerorientiert", value: "Formuliere den Text im technischen Stil, zielgerichtet für Entwickler oder Fachanwender, mit präziser Terminologie. " },
  ];

  @Watch("syncedShowDialog")
  async onShowDialogChanged(val: boolean) {
    if (this.syncedShowDialog) {
      // On dialog open
      this.chatMessages = [];
      this.newMessage = '';
      this.chatMessages.push({
        id: 1,
        message: "",
        markdownContent: this.syncedContent,
        fromAi: false
      });

      // Disable body scroll on iOS
      this.$nextTick(async () => {
        await this.$globalHelper.delay(10);
        const modal = document.querySelector('.modal');
        disableBodyScroll(modal);
        // Add global key handler for Ctrl+Enter to send message (capture mode)
        window.addEventListener('keydown', this.onKeyDown as EventListener, true);
      });
    } else {
      // Dialog closing
      clearAllBodyScrollLocks();
      window.removeEventListener('keydown', this.onKeyDown as EventListener, true);
    }
  }

  beforeDestroy() {
    this.syncedShowDialog = false;
    clearAllBodyScrollLocks();
    window.removeEventListener('keydown', this.onKeyDown as EventListener, true);
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === 'Return') {
      if (e.shiftKey) {
        // Shift + Enter: allow newline (default behavior)
        return;
      }
      // normal Enter: Send message
      e.preventDefault();
      this.sendMessage();
    }
  }

  async sendMessage() {
    if (!this.newMessage.trim()) return;

    this.isLoading = true;

    if (this.chatMessages.length == 1) {
      this.chatMessages[0].message = this.newMessage.trim();
    } else {
      // add user message
      const userMessage: MarkdownChatMessageVm = {
        id: this.chatMessages.length + 1,
        message: this.newMessage.trim(),
        markdownContent: "",
        fromAi: false
      };
      this.chatMessages.push(userMessage);
      this.$globalHelper.animateScrollToBottom("chatContent", 800);
    }

    let params: ParamSuggestAiTextRevision = {
      languageCode: this.selectedLanguageCode ?? "de",
      messages: this.chatMessages
    };

    // this.$emit('newChatMessage', userMessage);
    await rest.url("contentManager/suggestAiTextRevision")
      .post(params)
      .then((response) => {
        // console.log("markdownAiChatDlg response: ", response);
        if (this.isMarkdownChatMessageVmArray(response)) {
          this.newMessage = "";
          this.chatMessages = response as MarkdownChatMessageVm[];
          this.$globalHelper.animateScrollToBottom("chatContent", 800);
        } else {
          console.error("Response is not an array of MarkdownChatMessageVm: ", response);
          return;
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  isMarkdownChatMessageVm(obj: any): obj is MarkdownChatMessageVm {
      return typeof obj.id === 'number' && typeof obj.fromAi === 'boolean';
  }

  isMarkdownChatMessageVmArray(response: any): response is MarkdownChatMessageVm[] {
    if (!Array.isArray(response)) {
        return false;
    }

    return response.every(item => this.isMarkdownChatMessageVm(item));
  }

  onConfirmReplace(message: MarkdownChatMessageVm) {
    if (!message.markdownContent) return;
    this.selectedMessage = message;
    this.showConfirmationDialog = true;
  }

  onReplaceContent() {
    if (!this.selectedMessage?.markdownContent) return;

    this.syncedContent = this.selectedMessage.markdownContent;
    // this.$emit('saveChatMessages', message.markdownContent);
    this.syncedShowDialog = false;
  }

  async onClose() {
    this.syncedShowDialog = false;
  }

  async onReset() {
    this.chatMessages = [];
    this.newMessage = '';
    this.chatMessages.push({
      id: 1,
      message: "",
      markdownContent: this.syncedContent,
      fromAi: false
    });
    this.$globalHelper.animateScrollToBottom("chatContent", 800);
  }

  private renderMarkdown(text: string): string {
    try {
      const rawHtml = marked(text, { async: false }) as string;
      return DOMPurify.sanitize(rawHtml);
    } catch (error) {
      console.error('Fehler beim Parsen von Markdown:', error);
      return text;
    }
  }
}
